<template>
  <div>
    <div class="custom-alert-snackbar">
      <v-alert
           v-if="alert.show"
           :type="alert.type"
           dismissible
           @click:close="alert.show = false"
           class="mb-4"
         >
           {{ alert.message }}
         </v-alert>
    </div>
    <DetailTemplate
      :custom-class="'web-setup-page'"
      v-if="getPermission('web-setup:view')"
    >
       
      <!-- Sidebar Navigation -->
      <template v-slot:sidebar-detail>
        <div class="sidebar-nav">
          <div class="nav-section">
            <div
              class="nav-item"
              :class="{ active: activeSection === 'booking-website' }"
              @click="setActiveSection('booking-website')"
            >
              <v-icon class="mr-2">mdi-web</v-icon>
              Booking Website
              <v-icon class="ml-auto" v-if="activeSection === 'booking-website'"
                >mdi-chevron-down</v-icon
              >
              <v-icon class="ml-auto" v-else>mdi-chevron-right</v-icon>
            </div>

            <div
              v-if="activeSection === 'booking-website'"
              class="nav-subsection"
            >
              <div
                class="nav-subitem"
                :class="{ active: activeSubSection === 'general' }"
                @click="setActiveSubSection('general')"
              >
                <v-icon class="mr-2" size="16">mdi-cog</v-icon>
                General
              </div>
              <!-- <div
                class="nav-subitem"
                :class="{ active: activeSubSection === 'footer' }"
                @click="setActiveSubSection('footer')"
              >
                <v-icon class="mr-2" size="16">mdi-page-layout-footer</v-icon>
                Footer
              </div> -->
              <div
                class="nav-subitem"
                :class="{ active: activeSubSection === 'faq' }"
                @click="setActiveSubSection('faq')"
              >
                <v-icon class="mr-2" size="16"
                  >mdi-frequently-asked-questions</v-icon
                >
                FAQ
              </div>
              <div
                v-if="false"
                class="nav-subitem"
                :class="{ active: activeSubSection === 'labels' }"
                @click="setActiveSubSection('labels')"
              >
                <v-icon class="mr-2" size="16">mdi-label</v-icon>
                Labels
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Header Title -->
      <template v-slot:header-title>
        <div class="header-title">
          <h2 class="mb-0">Customize</h2>
          <div class="breadcrumb-text">
            <span class="text-muted">Customize</span>
            <v-icon class="mx-2" size="16">mdi-chevron-right</v-icon>
            <span>{{ getBreadcrumbText() }}</span>
          </div>
        </div>
      </template>

      <!-- Header Actions -->
      <template v-slot:header-action>
        <v-btn
          v-if="activeSubSection === 'footer' && hasFooterChanges"
          color="success"
          @click="saveFooterLayout"
          :loading="savingFooter"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </template>
        
      <!-- Main Body Content -->
      <template v-slot:body>
       

        <!-- General Section -->
        <General
          v-if="activeSubSection === 'general'"
          ref="generalComponent"
          :saving="savingGeneral"
          @settings-changed="onGeneralSettingsChanged"
          @save-settings="saveGeneralSettings"
          @show-success="showSuccess"
          @show-error="showError"
        />

        <!-- Footer Section -->
        <Footer
          v-if="activeSubSection === 'footer'"
          ref="footerComponent"
          :initial-layout="footerLayout.selectedLayout"
          :initial-left-items="footerLayout.leftColumnItems"
          :initial-right-items="footerLayout.rightColumnItems"
          :initial-third-items="footerLayout.thirdColumnItems"
          :saving="savingFooter"
          @changes-made="onFooterChangesChanged"
          @save-layout="saveFooterLayout"
          @item-selected="onFooterItemSelected"
          @layout-changed="onFooterLayoutChanged"
        />

        <!-- FAQ Section -->
        <FAQ
          v-if="activeSubSection === 'faq'"
          ref="faqComponent"
          :saving="savingFaq"
          @save-faq="saveFaqData"
          @show-success="showSuccess"
          @show-error="showError"
        />

        <!-- Labels Section -->
        <Labels
          v-if="activeSubSection === 'labels'"
          ref="labelsComponent"
          :saving="savingLabels"
          @save-labels="saveLabelsData"  
          @labels-success="showSuccess"
          @labels-error="showError"
        />

      </template>
    </DetailTemplate>
  </div>
</template>


<script>
import DetailTemplate from "@/view/pages/partials/Detail-Template.vue";
import General from "@/view/pages/web-setup/General.vue";
import Footer from "@/view/pages/web-setup/Footer.vue";
import FAQ from "@/view/pages/web-setup/Faq.vue";
import Labels from "@/view/pages/web-setup/Labels.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { UPLOAD, POST } from "@/core/services/store/request.module";
import { mapState } from "vuex";

export default {
  name: "WebSetup",
  components: {
    DetailTemplate,
    General,
    Footer,
    FAQ,
    Labels,
  },

  data() {
    return {
      activeSection: "booking-website",
      activeSubSection: "general",

      // Loading states
      savingGeneral: false,
      savingFooter: false,
      savingFaq: false,
      savingLabels: false,

      // Change tracking
      hasGeneralChanges: false,
      hasFooterChanges: false,

      // Alert system
      alert: {
        show: false,
        type: "success",
        message: "",
      },

      // Settings data
      websiteSettings: {
        pageTitle: "",
        pageDescription: "",
        favicon: null,
        faviconPreview: "",
        bannerImage: null,
        bannerImagePreview: "",
        bookingImage: null,
        bookingImagePreview: "",
        dashboardImage: null,
        dashboardImagePreview: "",
      },

      appearanceSettings: {
        colorScheme: "#1976D2",
        themeBackgroundColor: "#FFFFFF",
        themeMode: "light",
        fontFamily: "Roboto",
      },

      footerLayout: {
        selectedLayout: "2-columns",
        leftColumnItems: [
          {
            id: 1,
            number: "1",
            name: "Banner Image",
            enabled: true,
            type: "banner",
          },
          { id: 2, number: "2", name: "FAQ", enabled: true, type: "faq" },
        ],
        rightColumnItems: [
          { id: 3, number: "3", name: "Labels", enabled: true, type: "labels" },
        ],
        thirdColumnItems: [],
      },

      selectedFooterItem: null,
    };
  },

  computed: {
    ...mapState({
      requestMessage: (state) => state.request.message,
      requestError: (state) => state.request.errors,
    }),
  },

  // REMOVE THE WATCHERS COMPLETELY - handle success/error directly in methods
  // watch: {
  //   // All watcher code removed
  // },

  methods: {
    getPermission() {
      return true;
    },

    setActiveSection(section) {
      this.activeSection = section;
      if (section === "booking-website") {
        this.activeSubSection = "general";
      }
    },

    setActiveSubSection(subSection) {
      this.activeSubSection = subSection;
    },

    getBreadcrumbText() {
      const breadcrumbMap = {
        general: "General Settings",
        footer: "Footer Section",
        faq: "FAQ Management",
        labels: "Labels Management",
      };
      return breadcrumbMap[this.activeSubSection] || "General Settings";
    },

    // Centralized notification methods
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      setTimeout(() => (this.alert.show = false), 5000);
    },

    showSuccess(message) {
      this.showAlert("success", message);
    },

    showError(message) {
      this.showAlert("error", message);
    },

    // Error handler
    handleError(error) {
      console.error("Error:", error);
      let message = "An error occurred";
      
      // Extract error message from different possible structures
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (typeof errors === 'object') {
          message = Object.values(errors).flat().join(', ');
        } else {
          message = errors.toString();
        }
      } else if (error?.message) {
        message = error.message;
      }
      
      // Show error based on current active section
      if (this.activeSubSection === 'labels' && this.$refs.labelsComponent) {
        this.$refs.labelsComponent.showError(message);
      } else {
        this.showError(message);
      }
    },

    // General Settings Methods
    onGeneralSettingsChanged() {
      this.hasGeneralChanges = true;
    },

    async saveGeneralSettings(settingsData = null) {
      this.savingGeneral = true;
      try {
        const dataToSave = settingsData || this.$refs.generalComponent.getSettingsData();
        const formData = new FormData();

        formData.append("website[page_title]", dataToSave.website.pageTitle || "");
        formData.append("website[page_description]", dataToSave.website.pageDescription || "");

        // Add the missing website logo upload handling
        if (dataToSave.website.websiteLogo instanceof File) {
          formData.append("website[website_logo]", dataToSave.website.websiteLogo);
        }

        if (dataToSave.website.favicon instanceof File) {
          formData.append("website[favicon]", dataToSave.website.favicon);
        }
        if (dataToSave.website.bannerImage instanceof File) {
          formData.append("website[banner_image]", dataToSave.website.bannerImage);
        }
        if (dataToSave.website.bookingImage instanceof File) {
          formData.append("website[booking_image]", dataToSave.website.bookingImage);
        }
        if (dataToSave.website.dashboardImage instanceof File) {
          formData.append("website[dashboard_image]", dataToSave.website.dashboardImage);
        }

        formData.append("appearance[color_scheme]", dataToSave.appearance.colorScheme || "");
        formData.append("appearance[theme_background_color]", dataToSave.appearance.themeBackgroundColor || "");
        formData.append("appearance[font_family]", dataToSave.appearance.fontFamily || "");

        const response = await this.$store.dispatch(UPLOAD, {
          url: "/web-setup/general",
          data: formData,
        });

        // Handle response directly - don't rely on watchers
        if (response && (response.status === true || response.success === true)) {
          this.hasGeneralChanges = false;
          this.showSuccess("General Settings saved successfully");
        } else if (response && response.status === false) {
          // Handle API error response
          const errorMessage = response.message || "Failed to save general settings";
          this.showError(errorMessage);
        } else {
          throw new Error("Failed to save general settings - Invalid response");
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.savingGeneral = false;
      }
    },

    // Footer Methods
    onFooterChangesChanged() {
      this.hasFooterChanges = true;
    },

    onFooterItemSelected(item) {
      this.selectedFooterItem = item;
    },

    onFooterLayoutChanged(layout) {
      this.footerLayout.selectedLayout = layout;
    },

    async saveFooterLayout(layoutData = null) {
      this.savingFooter = true;
      try {
        const dataToSave = layoutData || this.$refs.footerComponent.getLayoutData();

        const response = await this.$store.dispatch(POST, {
          url: "/web-setup/layout",
          data: dataToSave,
        });

        // Handle response directly
        if (response && (response.status === true || response.success === true)) {
          this.hasFooterChanges = false;
          this.footerLayout = {
            selectedLayout: dataToSave.layout,
            leftColumnItems: [...dataToSave.left_column],
            rightColumnItems: [...dataToSave.right_column],
            thirdColumnItems: [...dataToSave.third_column],
          };
          this.showSuccess("Footer layout saved successfully");
        } else if (response && response.status === false) {
          const errorMessage = response.message || "Failed to save layout changes";
          this.showError(errorMessage);
        } else {
          throw new Error("Failed to save layout changes - Invalid response");
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.savingFooter = false;
      }
    },

    // FAQ Methods
    async saveFaqData(faqData) {
      this.savingFaq = true;
      try {
        const response = await this.$store.dispatch(POST, {
          url: "/web-setup/faq-bulk-save",
          data: faqData,
        });

        // Handle response directly
        if (response && (response.status === true || response.success === true)) {
          this.showSuccess("FAQ items saved successfully");
        } else if (response && response.status === false) {
          const errorMessage = response.message || "Failed to save FAQ items";
          this.showError(errorMessage);
        } else {
          throw new Error("Failed to save FAQ items - Invalid response");
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.savingFaq = false;
      }
    },

    // Labels Methods
    async saveLabelsData(labelsData) {
      this.savingLabels = true;
      try {
        const response = await this.$store.dispatch(POST, {
          url: "/web-setup/labels",
          data: { labels: labelsData },
        });

        // Handle response directly
        if (response && (response.status === true || response.success === true)) {
          this.showSuccess("Labels updated successfully!");
        } else if (response && response.status === false) {
          const errorMessage = response.message || "Failed to save labels";
          this.showError(errorMessage);
        } else {
          throw new Error("Failed to save labels - Invalid response");
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.savingLabels = false;
      }
    },
  },

  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Web-Setup" }]);
  },
};
</script>

<style lang="scss" scoped>
.web-setup-page {
  .sidebar-nav {
    .nav-section {
      .nav-item {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        border-radius: 8px;
        margin-bottom: 4px;
        transition: all 0.2s ease;

        &:hover {
          background-color: #f5f5f5;
        }

        &.active {
          background-color: #e3f2fd;
          color: #1976d2;
        }
      }

      .nav-subsection {
        margin-left: 24px;
        margin-top: 8px;

        .nav-subitem {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          border-radius: 6px;
          margin-bottom: 2px;
          transition: all 0.2s ease;

          &:hover {
            background-color: #f5f5f5;
          }

          &.active {
            background-color: #e3f2fd;
            color: #1976d2;
          }
        }
      }
    }
  }
}
</style>